import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import {
  Button,
  Checkbox,
  Flex,
  FormField,
  Loader,
  Select,
  SelectOption,
  Spacer,
  TextInput,
  Typography,
  AcknowledgeModal,
  Modal
} from '@percent/lemonade'
import style from './StaticLinkForm.module.scss'
import { StaticLinkFormModel, StaticLinkFormProps } from './StaticLinkForm.types'
import { useRequiredParams } from '@percent/admin-dashboard/common/hooks'

const slugRegex = /^[a-z0-9-]+$/

export function StaticLinkForm({
  configurations,
  isLoadingConfigurations,
  onSubmit,
  isSaving,
  translationKeys,
  slugConfigModalStatus,
  setSlugConfigModalStatus,
  defaultValues: defaultValuesProp,
  defaultConfigurationOption
}: Readonly<StaticLinkFormProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: partnerId } = useRequiredParams()

  const validationSchema = yup.object().shape({
    slug: yup.string().required(t('errorMessage.required')).matches(slugRegex, t('errorMessage.slugFormat')),
    validationSubmissionConfigurationId: yup.string().required(t('errorMessage.required')),
    redirectUrl: yup.string(),
    isEnabled: yup.boolean(),
    enablePost: yup.boolean(),
    allowedOrigin: yup.string()
  })

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<StaticLinkFormModel>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      slug: '',
      validationSubmissionConfigurationId: '',
      redirectUrl: '',
      isEnabled: true,
      enablePost: false,
      allowedOrigin: '',
      ...defaultValuesProp
    }
  })

  const enablePost = watch('enablePost')
  const slug = watch('slug')

  const configurationOptions = configurations.map(config => ({
    value: config.id,
    label: config.name || config.id
  }))

  const onSave = () => {
    handleSubmit(formValue => {
      onSubmit(formValue)
    })()
  }

  const handleCloseSlugConfigModal = () => {
    setSlugConfigModalStatus(null)
  }

  const hasErrors = !!Object.keys(errors).length

  return (
    <div className={style.container}>
      <Flex className={style.pageHeader} justify="space-between" align="center">
        <Typography variant="h2">{t(translationKeys.title)}</Typography>
        <Flex gap={8}>
          <Button variant="secondary" size="small" onClick={() => navigate(-1)}>
            {t('button.cancel')}
          </Button>
          <Button variant="primary" size="small" onClick={onSave} disabled={hasErrors} loading={isSaving}>
            {t(translationKeys.submitButton)}
          </Button>
        </Flex>
      </Flex>
      <Spacer size={4} axis="horizontal" />
      <div className={style.content}>
        <Typography variant="small">{t('typography.createStaticLinkDescription')}</Typography>
        <Spacer size={6} axis="horizontal" />

        {isLoadingConfigurations ? (
          <Loader />
        ) : (
          <Flex direction="column" gap={16}>
            <Controller
              name="validationSubmissionConfigurationId"
              control={control}
              render={({ field: { onChange } }) => (
                <FormField
                  label={t('typography.configuration')}
                  status={errors.validationSubmissionConfigurationId ? 'danger' : undefined}
                  statusMessage={errors.validationSubmissionConfigurationId?.message}
                  necessity="required"
                >
                  <Select
                    placeholder={t('typography.selectConfiguration')}
                    searchable
                    options={configurationOptions}
                    defaultValue={defaultConfigurationOption}
                    onChange={(option: SelectOption) => onChange(option.value)}
                  />
                </FormField>
              )}
            />

            <Controller
              name="slug"
              control={control}
              render={({ field }) => (
                <FormField
                  label={t('typography.staticLinkSlug')}
                  status={errors.slug ? 'danger' : undefined}
                  statusMessage={errors.slug?.message}
                  necessity="required"
                  description={t('typography.staticLinkSlugHelper')}
                >
                  <TextInput placeholder={t('typography.urlSlugPlaceholder')} {...field} />
                </FormField>
              )}
            />

            <Controller
              name="redirectUrl"
              control={control}
              render={({ field }) => (
                <FormField
                  label={t('typography.redirectUrl')}
                  status={errors.redirectUrl ? 'danger' : undefined}
                  statusMessage={errors.redirectUrl?.message}
                >
                  <TextInput placeholder={t('typography.redirectUrlPlaceholder')} {...field} />
                </FormField>
              )}
            />

            <Controller
              name="isEnabled"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Checkbox
                  name={name}
                  label={t('typography.isEnabled')}
                  variant="default"
                  active={value}
                  onChange={onChange}
                />
              )}
            />

            <Controller
              name="enablePost"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Checkbox
                  name={name}
                  label={t('typography.enablePost')}
                  variant="default"
                  active={value}
                  onChange={onChange}
                />
              )}
            />

            {enablePost && (
              <Controller
                name="allowedOrigin"
                control={control}
                render={({ field }) => (
                  <FormField
                    label={t('typography.allowedOrigin')}
                    status={errors.allowedOrigin ? 'danger' : undefined}
                    statusMessage={errors.allowedOrigin?.message}
                  >
                    <TextInput placeholder={t('typography.allowedOriginPlaceholder')} {...field} />
                  </FormField>
                )}
              />
            )}
          </Flex>
        )}
      </div>
      <Modal open={slugConfigModalStatus !== null} onClose={handleCloseSlugConfigModal}>
        {slugConfigModalStatus === 'error' && (
          <AcknowledgeModal
            result="negative"
            title={t(translationKeys.errorTitle)}
            description={t(translationKeys.errorDescription, { slug })}
            buttonText={t('button.close')}
            handleClose={handleCloseSlugConfigModal}
          />
        )}
        {slugConfigModalStatus === 'success' && (
          <AcknowledgeModal
            result="positive"
            title={t(translationKeys.successTitle)}
            description={t(translationKeys.successDescription, { slug })}
            buttonText={t('button.close')}
            handleClose={() => {
              handleCloseSlugConfigModal()
              navigate(`/partners/${partnerId}/static-links`)
            }}
          />
        )}
      </Modal>
    </div>
  )
}
